import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { useAuth } from '../auth/useAuth';
import AdminLayout from '../components/AdminLayout';

const CashBoxManagement = () => {
  const { api } = useAuth();
  const [cashBoxes, setCashBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resetDialog, setResetDialog] = useState({ open: false, cashBox: null });
  const [resetReason, setResetReason] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    fetchCashBoxes();
  }, []);

  const fetchCashBoxes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/transactions/cash-box/all');
      setCashBoxes(response.data);
    } catch (error) {
      console.error('שגיאה בטעינת הקופות:', error);
      setError('שגיאה בטעינת פרטי הקופות');
    } finally {
      setLoading(false);
    }
  };

  const handleResetClick = (cashBox) => {
    setResetDialog({ open: true, cashBox });
    setResetReason('');
  };

  const handleResetConfirm = async () => {
    try {
      setResetLoading(true);
      await api.post(`/transactions/cash-box/${resetDialog.cashBox.id}/reset`, {
        reason: resetReason || 'איפוס קופה'
      });
      
      // רענון הנתונים
      await fetchCashBoxes();
      
      // סגירת הדיאלוג
      setResetDialog({ open: false, cashBox: null });
      setResetReason('');
    } catch (error) {
      console.error('שגיאה באיפוס הקופה:', error);
      setError('שגיאה באיפוס הקופה');
    } finally {
      setResetLoading(false);
    }
  };

  const getTotalCash = () => {
    if (!cashBoxes || cashBoxes.length === 0) {
      return 0;
    }
    
    return cashBoxes.reduce((total, cashBox) => {
      const amount = parseFloat(cashBox.currentAmount) || 0;
      return total + amount;
    }, 0);
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom align="center">
          ניהול קופות סדרנים
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h5" align="center" color="primary">
            💰 סה"כ מזומן בשטח: ${getTotalCash().toFixed(2)}
          </Typography>
        </Paper>

        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>סדרן</strong></TableCell>
                <TableCell align="center"><strong>יתרה נוכחית</strong></TableCell>
                <TableCell align="center"><strong>סה"כ נאסף</strong></TableCell>
                <TableCell align="center"><strong>איפוס אחרון</strong></TableCell>
                <TableCell align="center"><strong>פעולות</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cashBoxes.map((cashBox) => (
                <TableRow key={cashBox.id}>
                  <TableCell>
                    <Typography variant="body1">
                      {cashBox.user?.firstName} {cashBox.user?.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {cashBox.user?.email}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={`$${(parseFloat(cashBox.currentAmount) || 0).toFixed(2)}`}
                      color={(parseFloat(cashBox.currentAmount) || 0) > 100 ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body1">
                      ${(parseFloat(cashBox.totalCollected) || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {cashBox.lastResetAt 
                        ? new Date(cashBox.lastResetAt).toLocaleDateString('he-IL')
                        : 'לא בוצע'
                      }
                    </Typography>
                    {cashBox.lastResetBy && (
                      <Typography variant="caption" color="text.secondary">
                        על ידי: {cashBox.lastResetBy.firstName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      onClick={() => handleResetClick(cashBox)}
                      disabled={(parseFloat(cashBox.currentAmount) || 0) === 0}
                    >
                      איפוס קופה
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* דיאלוג איפוס קופה */}
        <Dialog open={resetDialog.open} onClose={() => setResetDialog({ open: false, cashBox: null })}>
          <DialogTitle>איפוס קופה</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              האם אתה בטוח שברצונך לאפס את הקופה של {resetDialog.cashBox?.user?.firstName}?
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              יתרה נוכחית: ${(parseFloat(resetDialog.cashBox?.currentAmount) || 0).toFixed(2)}
            </Typography>
            <TextField
              fullWidth
              label="סיבת איפוס (אופציונלי)"
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              margin="normal"
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetDialog({ open: false, cashBox: null })}>
              ביטול
            </Button>
            <Button 
              onClick={handleResetConfirm} 
              color="warning" 
              variant="contained"
              disabled={resetLoading}
            >
              {resetLoading ? <CircularProgress size={20} /> : 'אפס קופה'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </AdminLayout>
  );
};

export default CashBoxManagement;
